/**
 * Tiny date / age / plural helpers used by the member-card UI (and
 * safe to reuse anywhere else). All copy is Romanian — matches the
 * existing voice elsewhere in the app.
 *
 * No external dependencies on purpose (we don't need `date-fns` or
 * `dayjs` for this tiny surface).
 */

/**
 * Return a Romanian relative-time label for an ISO datetime string:
 *   - "acum câteva secunde"  (< 1 min)
 *   - "acum N minute"        (< 1 h)
 *   - "acum o oră" / "acum N ore" (< 1 day)
 *   - "azi"                  (today, > 1 h)
 *   - "ieri"                 (yesterday)
 *   - "acum N zile"          (< 7 days)
 *   - "acum o săptămână" / "acum N săptămâni" (< 31 days)
 *   - Falls back to a short formatted date for older timestamps.
 *
 * Returns null when the input is empty/invalid so callers can hide
 * the chip gracefully.
 */
export function formatRelativeTimeRo(iso?: string | null): string | null {
  if (!iso) return null;
  const then = new Date(iso);
  if (Number.isNaN(then.getTime())) return null;
  const now = new Date();
  const diffMs = now.getTime() - then.getTime();
  // Future timestamps (clock skew, edge cases): fall through to the
  // short date format so we don't say "acum -3 minute".
  if (diffMs < 0) return formatShortDateRo(then);

  const sec = Math.floor(diffMs / 1000);
  if (sec < 60) return 'acum câteva secunde';

  const min = Math.floor(sec / 60);
  if (min < 60) return `acum ${min} ${min === 1 ? 'minut' : 'minute'}`;

  const hr = Math.floor(min / 60);
  if (hr < 24) {
    // Same calendar day → "azi" reads better than "acum 5 ore" once
    // we cross the morning-vs-afternoon boundary, but "acum N ore"
    // is more precise for very recent activity. Use "azi" only when
    // we're past the same-day cutoff (≥6h) AND it's still today.
    const sameDay = now.toDateString() === then.toDateString();
    if (sameDay && hr >= 6) return 'azi';
    return `acum ${hr} ${hr === 1 ? 'oră' : 'ore'}`;
  }

  // Yesterday: relative to local midnight rather than naive 24h
  // because "ieri" feels off when something happened 23h ago at
  // 23:45 yesterday vs 00:30 today.
  const startOfToday = new Date(now);
  startOfToday.setHours(0, 0, 0, 0);
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  if (then >= startOfYesterday && then < startOfToday) return 'ieri';

  const days = Math.floor(hr / 24);
  if (days < 7) return `acum ${days} ${days === 1 ? 'zi' : 'zile'}`;

  const weeks = Math.floor(days / 7);
  if (days < 31) return `acum ${weeks} ${weeks === 1 ? 'săptămână' : 'săptămâni'}`;

  return formatShortDateRo(then);
}

/** "12 iun 2026" style short label. */
export function formatShortDateRo(d: Date | string | null | undefined): string | null {
  if (!d) return null;
  const dt = typeof d === 'string' ? new Date(d) : d;
  if (Number.isNaN(dt.getTime())) return null;
  return new Intl.DateTimeFormat('ro-RO', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
  }).format(dt);
}

/**
 * Compute an age label from an ISO date of birth.
 *
 *   - ≥ 1 year: "1 an" / "N ani"
 *   - < 1 year and ≥ 1 month: "1 lună" / "N luni"
 *   - < 1 month: "0 luni"
 *
 * Returns null for missing/invalid input so the caller can simply
 * hide the chip. Whole-year cutoff respects the birthday: someone
 * born 2020-06-13 is still "5 ani" on 2026-06-12, flipping to
 * "6 ani" on 2026-06-13.
 */
export function formatAgeRo(iso?: string | null): string | null {
  if (!iso) return null;
  const dob = new Date(iso);
  if (Number.isNaN(dob.getTime())) return null;

  const now = new Date();
  let years = now.getFullYear() - dob.getFullYear();
  const monthDelta = now.getMonth() - dob.getMonth();
  const dayDelta = now.getDate() - dob.getDate();
  if (monthDelta < 0 || (monthDelta === 0 && dayDelta < 0)) {
    years -= 1;
  }

  if (years >= 1) {
    return `${years} ${years === 1 ? 'an' : 'ani'}`;
  }

  // Under one year: count whole months elapsed since the birthday.
  let months = (now.getFullYear() - dob.getFullYear()) * 12 + monthDelta;
  if (dayDelta < 0) months -= 1;
  if (months < 0) months = 0;
  return `${months} ${months === 1 ? 'lună' : 'luni'}`;
}

/**
 * Romanian pluralisation for the "N documente" copy on the card.
 *
 * Keeps the rule simple per the spec:
 *   - 0 → "0 documente"
 *   - 1 → "1 document"
 *   - N → "N documente"
 */
export function formatDocumentsCountRo(n: number): string {
  const safe = Math.max(0, Math.floor(n || 0));
  return `${safe} ${safe === 1 ? 'document' : 'documente'}`;
}
